// src/services/comparisonService.js
//
// SINGLE-ROW PATH ONLY.
//
// Bulk marks calculation no longer runs here. It moved to the backend
// (shorthand-backend/services/marksCalculation.js) because scoring a whole
// department meant pulling ~206 MB of mistake word lists into the tab, which
// crashed it on department 17. What is left is the per-row "Calc" button, where
// one student is a few KB and a round trip through the server would buy nothing.
//
// calculateResultAndGrade below is therefore duplicated: this copy scores the
// single-row button, and services/marksCalculation.js scores everything else.
// THE TWO MUST BE CHANGED TOGETHER — editing only one silently makes the Calc
// button disagree with the calculated column next to it.
import axios from 'axios';
import { COMPARE_API_BASE_URL } from '../config';

/**
 * Configuration for comparison service.
 *
 * `/compare` and `/compare-batch` are served by the Python comparison API, not
 * by the main backend — this must point at REACT_APP_COMPARE_API_URL.
 */
const COMPARISON_API_BASE_URL = COMPARE_API_BASE_URL;

/**
 * Did the candidate submit anything for this passage?
 *
 * The blank-passage substitution (' ') keeps the comparison running for an
 * unattempted passage, so every model-answer word still comes back as an omission -
 * that is what the Student-wise report needs to show. The MARK, though, must be an
 * outright 0 rather than 50 - total/3 clamped at zero: the clamp only lands on 0
 * because a real GCC model answer runs to hundreds of words, and would leave marks
 * on the board for a short or truncated one.
 */
const hasAttempt = (value) => value != null && String(value).trim() !== '';

/**
 * Calculate result and grade based on marks
 * 
 * Mandatory order of logic (Rule 7):
 *   1. Apply rounding to individual passage marks
 *   2. Check minimum per-passage eligibility
 *   3. Determine grace eligibility (can the student pass with max 2 grace?)
 *   4. Apply grace minimally and conditionally
 *   5. Determine PASS / FAIL
 *   6. Assign grade (PASS only, with restrictions)
 * 
 * @param {number} marksA - Marks for Passage A
 * @param {number} marksB - Marks for Passage B (ignored when examType === 'SKILL')
 * @param {number} totalMarks - Total marks (unused; recalculated from rounded values)
 * @param {string} [examType] - 'GCC' (default) or 'SKILL'. SKILL has only Passage A,
 *   total out of 80, pass at >= 32, no grace, no grade letter.
 * @returns {Object} Result data including result, grade, rounded marks, and grace marks
 */
const calculateResultAndGrade = (marksA, marksB, totalMarks, examType) => {
  // ── Step 1: Rounding Rule ───────────────────────────────────────────
  // Decimal values up to 0.50 → round to the nearest 0.5
  //   Examples: 10.1 → 10.5, 10.50 → 10.50, 14.33 → 14.50
  // Decimal values above 0.50 → round up to next whole number
  //   Examples: 10.51 → 11, 10.6 → 11, 3.67 → 4.00
  const roundMarks = (marks) => {
    const num = parseFloat(marks);
    const wholePart = Math.floor(num);
    const decimalPart = num - wholePart;

    if (decimalPart === 0) return num;            // Exact whole number
    if (decimalPart <= 0.50) return wholePart + 0.5; // Up to 0.50 → x.5
    return Math.ceil(num);                         // Above 0.50 → round up
  };

  // ── SKILL exam branch ──────────────────────────────────────────────
  // SKILL has only Passage A, total out of 80, pass at >= 32 (40%), no grace,
  // no grade letter. Passage B / grace fields are returned as null so the UI
  // and Excel exports can display 'N/A'.
  if (examType === 'SKILL') {
    const roundedA = roundMarks(parseFloat(marksA));
    const passes = roundedA >= 32;
    const result = passes ? 'PASS' : 'FAIL';

    return {
      result,
      grade: '',
      roundedA: roundedA.toFixed(2),
      roundedB: null,
      roundedTotal: roundedA.toFixed(2),
      graceMarksA: 0,
      graceMarksB: 0,
      totalGrace: 0,
      roundedGraceA: roundedA.toFixed(2),
      roundedGraceB: null,
      finalMarks: roundedA.toFixed(2)
    };
  }

  const numMarksA = parseFloat(marksA);
  const numMarksB = parseFloat(marksB);

  // Apply rounding BEFORE any grace logic
  const roundedA = roundMarks(numMarksA);
  const roundedB = roundMarks(numMarksB);
  const roundedTotal = roundedA + roundedB;

  // ── Step 2: Check if student already passes without grace ───────────
  const alreadyPasses = roundedA >= 15 && roundedB >= 15 && roundedTotal >= 50;

  // Initialize grace marks and final values
  let graceMarksA = 0;
  let graceMarksB = 0;
  let totalGrace = 0;
  let finalA = roundedA;
  let finalB = roundedB;
  let finalTotal = roundedTotal;

  if (!alreadyPasses) {
    // ── Step 3: Determine grace eligibility ─────────────────────────
    // Check whether the student CAN pass with at most 2 grace marks.
    // If irrecoverable (needs > 2 grace), NO grace is applied at all (Rule 7).

    // Grace needed to bring each passage to the minimum 15
    const graceNeededForA = Math.max(0, 15 - roundedA);
    const graceNeededForB = Math.max(0, 15 - roundedB);
    const graceForPassageMins = graceNeededForA + graceNeededForB;

    if (graceForPassageMins <= 2) {
      // Passage minimums CAN be met with grace; check total requirement
      const tentativeA = roundedA + graceNeededForA;
      const tentativeB = roundedB + graceNeededForB;
      const tentativeTotal = tentativeA + tentativeB;

      // Additional grace needed to bring total to exactly 50
      const graceForTotal = Math.max(0, 50 - tentativeTotal);
      const totalGraceNeeded = graceForPassageMins + graceForTotal;

      if (totalGraceNeeded <= 2) {
        // ── Step 4: Apply grace minimally and conditionally ──────────
        // Student CAN pass — apply the minimum grace required.

        // First: bring passages to 15
        graceMarksA = graceNeededForA;
        graceMarksB = graceNeededForB;
        finalA = tentativeA;
        finalB = tentativeB;

        // Then: bring total to 50 if still short
        const remainingGrace = 2 - graceForPassageMins;
        if (tentativeTotal < 50 && remainingGrace > 0) {
          let additionalGrace = Math.min(50 - tentativeTotal, remainingGrace);
          // Distribute additional grace one mark at a time, balancing between passages
          while (additionalGrace > 0) {
            const increment = Math.min(1, additionalGrace);
            if (graceMarksA <= graceMarksB) {
              graceMarksA += increment;
              finalA += increment;
            } else {
              graceMarksB += increment;
              finalB += increment;
            }
            additionalGrace -= increment;
          }
        }

        totalGrace = graceMarksA + graceMarksB;
        finalTotal = finalA + finalB;
      }
      // else: total can't reach 50 even with max grace → stays FAIL, no grace
    }
    // else: passage minimums can't be met with max grace → stays FAIL, no grace
  }

  // ── Step 5: Determine PASS / FAIL ──────────────────────────────────
  const passes = finalA >= 15 && finalB >= 15 && finalTotal >= 50;
  const result = passes ? 'PASS' : 'FAIL';

  // If FAIL, ensure NO grace marks are returned (Rules 3, 6)
  // Failed students must not receive grace marks in final marks.
  if (result === 'FAIL') {
    graceMarksA = 0;
    graceMarksB = 0;
    totalGrace = 0;
    finalA = roundedA;
    finalB = roundedB;
    finalTotal = roundedTotal;
  }

  // ── Step 6: Assign grade (PASS only) ───────────────────────────────
  // Grades based on final marks (after rounding and grace):
  //   Grade A: finalTotal >= 75
  //   Grade B: 60 <= finalTotal < 75
  //   Grade C: 50 <= finalTotal < 60
  let grade = '';
  if (result === 'PASS') {
    if (finalTotal >= 75) {
      grade = 'A';
    } else if (finalTotal >= 60) {
      grade = 'B';
    } else {
      grade = 'C';
    }
  }

  return {
    result,
    grade,
    roundedA: roundedA.toFixed(2),
    roundedB: roundedB.toFixed(2),
    roundedTotal: roundedTotal.toFixed(2),
    graceMarksA,
    graceMarksB,
    totalGrace,
    roundedGraceA: finalA.toFixed(2),
    roundedGraceB: finalB.toFixed(2),
    finalMarks: finalTotal.toFixed(2)
  };
};

/**
 * Compare two passages and return mistake analysis
 * @param {string} answerPassage - The correct/answer passage
 * @param {string} userPassage - The user's submitted passage
 * @param {Array<string>} ignoreList - List of words to ignore in comparison
 * @returns {Promise<Object>} Comparison result with mistake counts
 */
export const comparePassages = async (answerPassage, userPassage, ignoreList = []) => {
  try {
    const response = await axios.post(`${COMPARISON_API_BASE_URL}/compare`, {
      text1: userPassage,
      text2: answerPassage,
      ignore_list: ignoreList,
      ignore_case: true
    });

    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error('Error comparing passages:', error);
    return {
      success: false,
      error: true,
      message: error.response?.status === 404
        ? `Comparison service not found (404) at ${COMPARISON_API_BASE_URL}/compare. Please check that the comparison API is running.`
        : error.code === 'ECONNREFUSED' || error.message.includes('Network Error')
        ? `Cannot connect to comparison service. Please ensure the API server is running on ${COMPARISON_API_BASE_URL}`
        : error.response?.data?.message || error.message || 'Failed to connect to comparison service'
    };
  }
};

/**
 * Compare passages for a single row and calculate complete results
 * @param {Object} row - The data row containing passage information
 * @returns {Promise<Object>} Complete comparison result with marks, mistakes, result, and grade
 */
export const comparePassagesForRow = async (row) => {
  const isSkill = row.examType === 'SKILL';

  // Use single space for empty/null passages to allow calculation
  const passageA = row.passageA && row.passageA.trim() !== '' ? row.passageA : ' ';
  const ansPassageA = row.ansPassageA && row.ansPassageA.trim() !== '' ? row.ansPassageA : ' ';

  // Parse ignored words for each passage
  const ignoreListA = row.QPA
    ? row.QPA.split(',').map(word => word.trim()).filter(word => word.length > 0)
    : [];

  // Compare Passage A
  const resultA = await comparePassages(ansPassageA, passageA, ignoreListA);
  if (!resultA.success) return resultA;
  const mistakesA = resultA.data;

  // Compare Passage B (skip for SKILL — SKILL has no Passage B)
  let mistakesB = null;
  if (!isSkill) {
    const passageB = row.passageB && row.passageB.trim() !== '' ? row.passageB : ' ';
    const ansPassageB = row.ansPassageB && row.ansPassageB.trim() !== '' ? row.ansPassageB : ' ';
    const ignoreListB = row.QPB
      ? row.QPB.split(',').map(word => word.trim()).filter(word => word.length > 0)
      : [];
    const resultB = await comparePassages(ansPassageB, passageB, ignoreListB);
    if (!resultB.success) return resultB;
    mistakesB = resultB.data;
  }

  // Calculate mistakes for Passage A
  const spellingA = mistakesA.spelling?.length || 0;
  const missedA = mistakesA.missed?.length || 0;
  const addedA = mistakesA.added?.length || 0;
  const grammarA = mistakesA.grammar?.length || 0;
  const totalA = spellingA + missedA + addedA + grammarA;

  // Calculate mistakes for Passage B (zero for SKILL)
  const spellingB = mistakesB ? (mistakesB.spelling?.length || 0) : 0;
  const missedB = mistakesB ? (mistakesB.missed?.length || 0) : 0;
  const addedB = mistakesB ? (mistakesB.added?.length || 0) : 0;
  const grammarB = mistakesB ? (mistakesB.grammar?.length || 0) : 0;
  const totalB = spellingB + missedB + addedB + grammarB;

  // Calculate total mistakes from both passages
  const totalSpelling = spellingA + spellingB;
  const totalMissed = missedA + missedB;
  const totalAdded = addedA + addedB;
  const totalGrammar = grammarA + grammarB;
  const totalMistakes = totalSpelling + totalMissed + totalAdded + totalGrammar;

  // Calculate marks for Passage A. A blank submission is a complete omission of the
  // passage, not a partial attempt, so it scores 0 outright. Kept in step with
  // shorthand-backend/services/marksCalculation.js.
  let marksA;
  if (!hasAttempt(row.passageA)) {
    marksA = 0;
  } else if (isSkill) {
    marksA = 80 - (totalA / 2);
  } else {
    marksA = 50 - (totalA / 3);
  }
  marksA = Math.max(0, marksA);

  // Calculate marks for Passage B (null for SKILL — no Passage B)
  let marksB = null;
  if (!isSkill) {
    marksB = hasAttempt(row.passageB) ? 50 - (totalB / 3) : 0;
    marksB = Math.max(0, marksB);
  }

  // Calculate total marks (Passage A only for SKILL)
  const totalMarks = isSkill ? marksA : marksA + marksB;

  // Calculate result and grade
  const resultData = calculateResultAndGrade(
    marksA.toFixed(2),
    isSkill ? null : marksB.toFixed(2),
    totalMarks.toFixed(2),
    row.examType
  );

  return {
    success: true,
    spelling: totalSpelling,
    missed: totalMissed,
    added: totalAdded,
    grammar: totalGrammar,
    total: totalMistakes,
    marks: totalMarks.toFixed(2),
    // Passage A details
    spellingA,
    missedA,
    addedA,
    grammarA,
    totalA,
    marksA: marksA.toFixed(2),
    // Passage B details (null for SKILL)
    spellingB: isSkill ? null : spellingB,
    missedB: isSkill ? null : missedB,
    addedB: isSkill ? null : addedB,
    grammarB: isSkill ? null : grammarB,
    totalB: isSkill ? null : totalB,
    marksB: isSkill ? null : marksB.toFixed(2),
    // Result and Grade
    result: resultData.result,
    grade: resultData.grade,
    roundedA: resultData.roundedA,
    roundedB: resultData.roundedB,
    roundedTotal: resultData.roundedTotal,
    graceMarksA: resultData.graceMarksA,
    graceMarksB: resultData.graceMarksB,
    totalGrace: resultData.totalGrace,
    roundedGraceA: resultData.roundedGraceA,
    roundedGraceB: resultData.roundedGraceB,
    finalMarks: resultData.finalMarks,
    mistakesA,
    mistakesB
  };
};


// Export all functions as default object
const comparisonService = {
  comparePassages,
  comparePassagesForRow
};

export default comparisonService;
